import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { analytics } from '@/analytics';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import {
    fetchBehaviorOverview,
    fetchRespiration,
    fetchSwimSpeed,
    fetchTilt,
} from '@/domain/behavior';
import { sanitizeFileName } from '@/utils/fileNameFormatting';
import { saveBlob } from '@/utils/saveBlob';

import {
    DAILY_EXPORT_COLUMNS,
    DAYS_TO_EXPORT,
    ENVIRONMENTAL_EXPORT_COLUMNS,
} from '../helpers/behaviorExportColumns';
import { generateCSV, generateXLSX, tableFromRows } from '../helpers/behaviorExportFiles';
import { combineBehaviorExportData } from '../helpers/combineBehaviorExportData';

function getLast30DaysRange(): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - DAYS_TO_EXPORT);

    return {
        from: from.toISOString().split('T')[0],
        to: to.toISOString().split('T')[0],
    };
}

export interface UseBehaviorExportResult {
    isExporting: boolean;
    error: Error | null;
    exportCSV: () => Promise<void>;
    exportXLSX: () => Promise<void>;
}

/**
 * Hook to handle behavior data export
 * Fetches last 30 days of data from swim speed, respiration, and tilt endpoints
 * and exports as CSV or XLSX with properly translated column names
 */
export function useBehaviorExport(cageId: number): UseBehaviorExportResult {
    const { t } = useTranslation('behaviorModule');
    const { location } = useCage();
    const cageName = useCageName();
    const { selectedFishGroupId } = useCageFilter();
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const queryClient = useQueryClient();

    const fetchExportData = useCallback(async () => {
        const dateRange = getLast30DaysRange();
        const scope = { fishGroupId: selectedFishGroupId };
        const scopedRange = { ...dateRange, ...scope };

        const [overviewData, swimSpeedData, respirationData, tiltData] = await Promise.all([
            fetchBehaviorOverview(queryClient, cageId, scope),
            fetchSwimSpeed(queryClient, cageId, scopedRange),
            fetchRespiration(queryClient, cageId, scopedRange),
            fetchTilt(queryClient, cageId, scopedRange),
        ]);

        const { dailyRows, environmentalSnapshot } = combineBehaviorExportData(
            swimSpeedData.data,
            respirationData.data,
            tiltData.data,
            overviewData
        );

        const daily = tableFromRows(DAILY_EXPORT_COLUMNS, dailyRows, t);
        const environmental = tableFromRows(
            ENVIRONMENTAL_EXPORT_COLUMNS,
            [environmentalSnapshot],
            t
        );

        const filename = sanitizeFileName(
            [location?.name ?? '', cageName ?? '', 'Optoscale-Behavior-Data', dateRange.to]
                .filter((x) => x)
                .join('-')
        );

        return {
            dailyTitle: t('export.behaviorData'),
            environmentalTitle: t('export.hourlyEnvironmentalData'),
            environmentalHeader: environmental.header,
            environmentalRow: environmental.rows[0] ?? [],
            dailyHeader: daily.header,
            dailyRows: daily.rows,
            filename,
        };
    }, [queryClient, cageId, selectedFishGroupId, t, location, cageName]);

    const exportCSV = useCallback(async () => {
        setIsExporting(true);
        setError(null);

        try {
            const {
                dailyTitle,
                environmentalTitle,
                environmentalHeader,
                environmentalRow,
                dailyHeader,
                dailyRows,
                filename,
            } = await fetchExportData();
            analytics.exportTriggered({
                exportType: 'behavior',
                fileFormat: 'csv',
            });
            const blob = generateCSV(
                dailyTitle,
                dailyHeader,
                dailyRows,
                environmentalTitle,
                environmentalHeader,
                environmentalRow
            );
            saveBlob(blob, `${filename}.csv`);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to export CSV'));
        } finally {
            setIsExporting(false);
        }
    }, [fetchExportData]);

    const exportXLSX = useCallback(async () => {
        setIsExporting(true);
        setError(null);

        try {
            const {
                dailyTitle,
                environmentalTitle,
                environmentalHeader,
                environmentalRow,
                dailyHeader,
                dailyRows,
                filename,
            } = await fetchExportData();
            analytics.exportTriggered({
                exportType: 'behavior',
                fileFormat: 'xlsx',
            });
            const blob = await generateXLSX(
                dailyTitle,
                dailyHeader,
                dailyRows,
                environmentalTitle,
                environmentalHeader,
                environmentalRow
            );
            saveBlob(blob, `${filename}.xlsx`);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to export XLSX'));
        } finally {
            setIsExporting(false);
        }
    }, [fetchExportData]);

    return {
        isExporting,
        error,
        exportCSV,
        exportXLSX,
    };
}
